import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(() => authService.getStoredUser());
  const [isLoading, setIsLoading] = useState(true);

  // Refresh user from API on mount to pick up any server-side changes
  useEffect(() => {
    const restore = async () => {
      if (!authService.isAuthenticated()) {
        setIsLoading(false);
        return;
      }
      try {
        const freshUser = await authService.getProfile();
        authService.storeUser(freshUser);
        setUserState(freshUser);
      } catch {
        // Token may be expired — authService.logout will clear it
      } finally {
        setIsLoading(false);
      }
    };
    restore();
  }, []);

  const setUser = useCallback((userData) => {
    authService.storeUser(userData);
    setUserState(userData);
  }, []);

  const clearUser = useCallback(() => {
    authService.clearUser();
    setUserState(null);
  }, []);

  // Users without adminRoleId are unrestricted super admins — they pass all permission checks.
  const hasPermission = useCallback((permissionKey) => {
    if (!user) return false;
    if (!user.adminRoleId) return true;
    const permissions = user.adminRole?.permissions ?? [];
    return permissions.includes(permissionKey);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, clearUser, hasPermission, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

export default AuthContext;
